import { useRouter } from '@tanstack/react-router';
import { ConnectorRouteView, JourneysConnectorHandoff, useConnectorHandoffHrefs } from '@/features/integrations';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { cdpPipelinesUrl, type CdpConnector } from './redirects';

/**
 * `/integrations/cdp_in` and `/integrations/cdp_out` are not pages: each one
 * exists only to send the reader into the Pipelines app, which is where CDP
 * connections are actually managed. The integrations directory links to both.
 *
 * The redirect replaces its history entry, so Back returns to the directory the
 * reader came from rather than bouncing them straight back out again.
 */
type CdpRouteProps = {
  connector: CdpConnector;
};

/** Both spellings of one connector's URL. Spelled out so each stays a typed route id. */
function useHandoffHrefs(connector: CdpConnector) {
  const router = useRouter();

  return useConnectorHandoffHrefs({
    buildMigrated: (workspaceId) =>
      connector === 'cdp_in'
        ? router.buildLocation({ to: '/workspaces/$workspaceId/integrations/cdp_in', params: { workspaceId } }).href
        : router.buildLocation({ to: '/workspaces/$workspaceId/integrations/cdp_out', params: { workspaceId } }).href,
    buildLegacy: (workspaceId) =>
      connector === 'cdp_in'
        ? router.buildLocation({ to: '/workspaces/$workspaceId/journeys/integrations/cdp_in', params: { workspaceId } })
            .href
        : router.buildLocation({
            to: '/workspaces/$workspaceId/journeys/integrations/cdp_out',
            params: { workspaceId },
          }).href,
  });
}

/** The old `/journeys/integrations/cdp_*` URL, which the directory still links to. */
export function JourneysCdpHandoff({ connector }: CdpRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SegmentCdpIntegrationsMigration);
  const { migrated } = useHandoffHrefs(connector);

  return <JourneysConnectorHandoff isMigrated={isMigrated} migratedHref={migrated} />;
}

/**
 * The migrated `/integrations/cdp_*` URL.
 *
 * `ConnectorRouteView` applies the gates Ember's `integrations` root route applied
 * to everything nested under it, which these routes inherited — a Builder-plan
 * account reached the available-plans page rather than Pipelines. The redirect
 * happens inside those gates for the same reason.
 */
export function CdpRouteView({ connector }: CdpRouteProps) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.SegmentCdpIntegrationsMigration);
  const { legacy } = useHandoffHrefs(connector);

  return (
    <ConnectorRouteView isMigrated={isMigrated} legacyHref={legacy}>
      {(workspaceId) => (
        <SafeNavigate to={cdpPipelinesUrl(connector, workspaceId)} options={{ shouldReplaceHistoryEntry: true }} />
      )}
    </ConnectorRouteView>
  );
}
